'use client';

/**
 * Global keyboard shortcuts reference.
 *
 * Listens for `?` key press (when focus isn't in a text input) and opens a
 * modal listing every platform-wide keyboard shortcut. Mounted once at the
 * dashboard layout level — no per-page wiring needed.
 */

import React, { useEffect, useState } from 'react';
import { Dialog } from '@statura/ui';

interface Shortcut {
  keys: string[];
  description: string;
  scope?: string;
}

const SHORTCUTS: Shortcut[] = [
  // Navigation
  { keys: ['⌘', 'K'], description: 'Open command palette / search', scope: 'Navigation' },
  {
    keys: ['Ctrl', 'K'],
    description: 'Open command palette (Windows / Linux)',
    scope: 'Navigation',
  },
  { keys: ['?'], description: 'Open this keyboard shortcuts reference', scope: 'Navigation' },
  { keys: ['Esc'], description: 'Close the current dialog, popover, or menu', scope: 'Navigation' },
  // Help chat
  { keys: ['Alt', 'Shift', 'H'], description: 'Open help chat', scope: 'Help chat' },
  // Forms
  { keys: ['Enter'], description: 'Submit form / confirm primary action', scope: 'Forms' },
  { keys: ['Shift', 'Enter'], description: 'New line inside a text area', scope: 'Forms' },
  { keys: ['Tab'], description: 'Move to next field / button', scope: 'Forms' },
  { keys: ['Shift', 'Tab'], description: 'Move to previous field / button', scope: 'Forms' },
];

/**
 * Should `?` trigger the shortcuts modal when pressed? No, when focus is in a
 * text-editable field — the user is typing a literal question mark.
 *
 * Covers native form elements, contenteditable regions, ARIA textbox, and
 * ARIA combobox (SearchableSelect shadow inputs render as combobox).
 */
function isTextInputFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if ((el as HTMLElement).isContentEditable) return true;
  const role = (el as HTMLElement).getAttribute('role');
  if (role === 'textbox' || role === 'combobox' || role === 'searchbox') return true;
  return false;
}

export function KeyboardShortcutsHint() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.repeat) return;
      if (e.key === '?' && !isTextInputFocused() && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const grouped = SHORTCUTS.reduce<Record<string, Shortcut[]>>((acc, s) => {
    const scope = s.scope ?? 'Other';
    acc[scope] = acc[scope] ?? [];
    acc[scope].push(s);
    return acc;
  }, {});

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      title="Keyboard shortcuts"
      description="Available anywhere in the platform. Press ? at any time to see this list again."
      size="md"
    >
      <div className="space-y-5">
        {Object.entries(grouped).map(([scope, items]) => (
          <section key={scope}>
            <h3 className="mb-2 text-eyebrow uppercase tracking-wider text-text-muted">{scope}</h3>
            <ul className="space-y-1.5">
              {items.map((s, i) => (
                <li key={i} className="flex items-center justify-between gap-4 text-body">
                  <span className="text-text-body">{s.description}</span>
                  <span className="flex items-center gap-1">
                    {s.keys.map((k, j) => (
                      <React.Fragment key={j}>
                        {j > 0 && <span className="text-caption text-slate-300">+</span>}
                        <kbd className="rounded-control border border-border bg-surface-sheet px-1.5 py-0.5 font-mono text-caption text-text-body">
                          {k}
                        </kbd>
                      </React.Fragment>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}
        <p className="text-caption text-text-muted pt-2 border-t border-border">
          Additional shortcuts may exist within individual modules. Press{' '}
          <kbd className="rounded-control border border-border bg-surface-sheet px-1 py-0.5 font-mono text-caption">
            ⌘K
          </kbd>{' '}
          to browse them via the command palette.
        </p>
      </div>
    </Dialog>
  );
}
